import { ACCOUNT_PAGE_SIZE } from 'constants/account';
import { AccountListReply, OpenTradeWarningReply } from 'proto/account-manager_pb';

export type OpenTradeAccount = {
  accountId: string;
  label: string;
};

type AccountClient = {
  getAccountList: (req: { userId: string; pageSize: number; paginationCursor: string; searchPattern: string }) => Promise<unknown>;
  openTradeWarning: (req: { userId: string; accountId: string; isConfig: boolean; slaveIdsList: string[] }) => Promise<unknown>;
};

function accountLabel(account: AccountListReply.AsObject['accountsList'][number]) {
  return account.nickname || account.accountNumber || account.accountId;
}

// Pausing disconnects every account and never closes a trade, so a copied one is left unmanaged: every
// account is checked, disabled or not connected too. Account Manager's `allow: false` means it found copied
// trades still open. Read a page at the server's cap at a time: a bigger page is refused outright.
export type OpenTradeCheck = {
  /** Accounts holding open copied trades. */
  found: OpenTradeAccount[];
  /** Accounts whose own check failed, so the review can name them rather than guess. */
  unchecked: OpenTradeAccount[];
};

export async function findAccountsWithOpenTrades(client: AccountClient, userId: string): Promise<OpenTradeCheck> {
  const found: OpenTradeAccount[] = [];
  const unchecked: OpenTradeAccount[] = [];
  let cursor = '';
  do {
    const page = (await client.getAccountList({
      userId,
      pageSize: ACCOUNT_PAGE_SIZE,
      paginationCursor: cursor,
      searchPattern: ''
    })) as AccountListReply.AsObject;

    const accounts = page.accountsList || [];
    // One account failing must not hide what the others found.
    const checks = await Promise.allSettled(
      accounts.map((account) => client.openTradeWarning({ userId, accountId: account.accountId, isConfig: false, slaveIdsList: [] }))
    );
    checks.forEach((check, i) => {
      const account = { accountId: accounts[i].accountId, label: accountLabel(accounts[i]) };
      if (check.status === 'rejected') unchecked.push(account);
      else if (!(check.value as OpenTradeWarningReply.AsObject).allow) found.push(account);
    });

    // A cursor that repeats would loop for ever; stop at the last page or a stuck one.
    const next = page.nextPageCursor || '';
    cursor = next === cursor ? '' : next;
  } while (cursor);

  return { found, unchecked };
}
